import { useCallback, useMemo } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useQueryScope } from "@/hooks/use-query-scope";
import { useScopedQuery } from "@/hooks/use-scoped-query";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields, listChanges } from "@/lib/changed-fields";
import {
  invalidateJobRoles,
  jobRoleMembersKey,
  jobRoleMembersQuery,
  jobRoleOptionsQuery,
  jobRolesKey,
  jobRolesQuery,
  myJobRoleKey,
  myJobRoleQuery,
  patchJobRoles,
} from "@/queries/job-roles";
import {
  projectJobRoleRecommendationsKey,
  projectJobRoleRecommendationsQuery,
} from "@/queries/project-resources";
import { roleAssignmentCandidatesChanged } from "@/queries/role-assignment-candidates";
import type { QueryScope } from "@/queries/scope";
import type { TMyMembership } from "@/services/workspace-job-role.service";
import { useStore } from "@/stores/store-context";
import type { TWorkItemAssigneeDetail } from "@/types/issue";
import type {
  TCreateJobRole,
  TJobRole,
  TJobRoleMember,
  TJobRoleRecommendation,
  TJobRoleSummary,
  TUpdateJobRole,
} from "@/types/job-role";

/** One empty list each, so a reader's memos see the same value until data loads. */
const NO_ROLES: TJobRole[] = [];
const NO_OPTIONS: TJobRoleSummary[] = [];
const NO_MEMBERS: TJobRoleMember[] = [];
const NO_RECOMMENDATIONS: TJobRoleRecommendation[] = [];

interface JobRoleReadOptions {
  /** `false` reads nothing, e.g. for a viewer the read would 403. */
  readonly enabled?: boolean;
}

/** A job-role read and whether it has answered. */
export interface JobRoleRead<T> {
  readonly data: T;
  /** The read has answered, so an empty `data` really is empty. */
  readonly loaded: boolean;
  /** The read is waiting on its first answer; a failed read has settled. */
  readonly isLoading: boolean;
  /** The read failed, or `null`. */
  readonly error: Error | null;
}

/** The shared shape of the job-role reads. */
function answer<T>(
  query: { data: T | undefined; isSuccess: boolean; isPending: boolean; error: Error | null },
  mode: string,
  on: boolean,
  empty: T,
): JobRoleRead<T> {
  return {
    data: query.data ?? empty,
    loaded: query.isSuccess,
    isLoading: on && (mode === "waiting" || query.isPending),
    error: query.error,
  };
}

/**
 * The workspace's job roles, with counts (`jobRoles.list`, NG-2802): the
 * settings list and the filters' bank. Needs `job_role.read`; a work-item
 * picker reads `useJobRoleOptions` instead. The reader observes the cached
 * query, so a job-role write in this tab, or another tab's through the
 * `job-roles:bank` hint, re-reads what it shows.
 *
 * Reads the signed-in user from a store, so the caller must be an `observer`.
 */
export function useJobRoles(
  workspaceSlug: string | undefined,
  { enabled = true }: JobRoleReadOptions = {},
): JobRoleRead<TJobRole[]> {
  const { workspaceJobRoleService } = useStore();
  const { mode, query } = useScopedQuery(
    workspaceSlug,
    (scope) => jobRolesQuery(workspaceJobRoleService, scope),
    { enabled },
  );
  return answer(query, mode, enabled && Boolean(workspaceSlug), NO_ROLES);
}

/** The work-item pickers' job-role choices (`jobRoles.options`, NG-2099). */
export function useJobRoleOptions(
  workspaceSlug: string | undefined,
  { enabled = true }: JobRoleReadOptions = {},
): JobRoleRead<TJobRoleSummary[]> {
  const { workspaceJobRoleService } = useStore();
  const { mode, query } = useScopedQuery(
    workspaceSlug,
    (scope) => jobRoleOptionsQuery(workspaceJobRoleService, scope),
    { enabled },
  );
  return answer(query, mode, enabled && Boolean(workspaceSlug), NO_OPTIONS);
}

/** One role's eligible members, each flagged `assigned` (`jobRoles.members`). */
export function useJobRoleMembers(
  workspaceSlug: string | undefined,
  jobRoleId: string,
  { enabled = true }: JobRoleReadOptions = {},
): JobRoleRead<TJobRoleMember[]> {
  const { workspaceJobRoleService } = useStore();
  const on = enabled && Boolean(jobRoleId);
  const { mode, query } = useScopedQuery(
    workspaceSlug,
    (scope) => jobRoleMembersQuery(workspaceJobRoleService, scope, jobRoleId),
    { enabled: on },
  );
  return answer(query, mode, on && Boolean(workspaceSlug), NO_MEMBERS);
}

/**
 * Read one role's members fresh from the server, whatever the cache holds,
 * and leave the answer in `jobRoles.members` for the readers on it. For a
 * picker whose save replaces the whole set: it must seed its draft from the
 * server's current members, never a cached copy another tab may have outdated.
 */
export function useFetchJobRoleMembers(
  workspaceSlug: string,
): (jobRoleId: string) => Promise<TJobRoleMember[]> {
  const { workspaceJobRoleService } = useStore();
  const client = useQueryClient();
  const scope = useQueryScope(workspaceSlug);
  return useCallback(
    (jobRoleId: string) => {
      if (!scope) return Promise.reject(new Error("[job-roles] read before the session resolved"));
      return client.fetchQuery({
        ...jobRoleMembersQuery(workspaceJobRoleService, scope, jobRoleId),
        staleTime: 0,
      });
    },
    [client, scope, workspaceJobRoleService],
  );
}

/** The signed-in member's own membership: their job role and workspace role. */
export function useMyMembership(
  workspaceSlug: string | undefined,
  { enabled = true }: JobRoleReadOptions = {},
): JobRoleRead<TMyMembership | null> {
  const { workspaceJobRoleService } = useStore();
  const { mode, query } = useScopedQuery(
    workspaceSlug,
    (scope) => myJobRoleQuery(workspaceJobRoleService, scope),
    { enabled },
  );
  return answer<TMyMembership | null>(query, mode, enabled && Boolean(workspaceSlug), null);
}

/** A project's Smart Assignment recommendations (R13), read while `enabled`. */
export function useJobRoleRecommendations(
  workspaceSlug: string | undefined,
  projectId: string,
  { enabled = true }: JobRoleReadOptions = {},
): JobRoleRead<TJobRoleRecommendation[]> {
  const { workspaceJobRoleService } = useStore();
  const on = enabled && Boolean(projectId);
  const { mode, query } = useScopedQuery(
    workspaceSlug,
    (scope) => projectJobRoleRecommendationsQuery(workspaceJobRoleService, scope, projectId),
    { enabled: on },
  );
  return answer(query, mode, on && Boolean(workspaceSlug), NO_RECOMMENDATIONS);
}

/**
 * After a write that changes which work a job role has on a project (a work
 * item's job role set or cleared): mark that project's cached recommendations
 * stale, so the Smart Assignment banner re-reads them if it is on screen and
 * the next read does not answer with the recommendations from before.
 */
export function useInvalidateJobRoleRecommendations(
  workspaceSlug: string,
): (projectId: string) => void {
  const client = useQueryClient();
  const scope = useQueryScope(workspaceSlug);
  return useCallback(
    (projectId: string) => {
      if (!scope || !projectId) return;
      client
        .invalidateQueries({
          queryKey: projectJobRoleRecommendationsKey(scope, projectId),
          exact: true,
        })
        .catch((error: unknown) => {
          console.warn("[job-roles] invalidating recommendations failed", {
            slug: workspaceSlug,
            projectId,
            error,
          });
        });
    },
    [client, scope, workspaceSlug],
  );
}

export interface JobRoleWriters {
  createJobRole(data: TCreateJobRole, source?: AnalyticsSource): Promise<TJobRole>;
  updateJobRole(id: string, data: TUpdateJobRole, source?: AnalyticsSource): Promise<TJobRole>;
  deleteJobRole(id: string, source?: AnalyticsSource): Promise<void>;
  /** Optimistic: the row shows the new status at once and goes back on failure. */
  setStatus(id: string, status: TJobRole["status"], source?: AnalyticsSource): Promise<void>;
  /** Replace a role's members; `memberIds` is the whole new set. */
  setJobRoleMembers(id: string, memberIds: string[], source?: AnalyticsSource): Promise<void>;
  /** Set (or clear) the signed-in member's own job role. */
  setMyJobRole(jobRoleId: string | null, source?: AnalyticsSource): Promise<TMyMembership>;
  /** Bulk-assign a role's project work to one person (Smart Assignment). */
  smartAssign(
    projectId: string,
    jobRoleId: string,
    assigneeId: string,
    assigneeDetail?: TWorkItemAssigneeDetail,
    source?: AnalyticsSource,
  ): Promise<void>;
}

/** Re-read every cached job-role read; a failure only warns, the write landed. */
function settle(client: QueryClient, scope: QueryScope | null, op: string, id: string) {
  if (!scope) return;
  invalidateJobRoles(client, scope).catch((error: unknown) => {
    console.warn("[job-roles] invalidating the cached job roles failed", {
      slug: scope.tenant,
      op,
      id,
      error,
    });
  });
}

/**
 * The job-role writers (NG-2802). Each resolves once the server has the write,
 * and rejects when it refused, so the caller keeps its own toast and save
 * state.
 *
 * A write puts its answer into the cached reads it changed at once (the list,
 * a role's members, the signed-in member's own), then marks every job-role
 * read stale, the pickers' options included, so what is on screen re-reads.
 * Where a write changes what the members list shows (a member's job role), it
 * marks that stale too; where the backend unassigns a role from work (a
 * delete, a deactivation), the work-item cache drops it. Each emits its event
 * once the write lands, never on a failure (NG-2063).
 *
 * Reads the signed-in user from the user store, so the caller must be an
 * `observer`.
 */
export function useJobRoleWriters(workspaceSlug: string): JobRoleWriters {
  const {
    workspaceJobRoleService: service,
    workspaceMembers: members,
    issue: issueStore,
  } = useStore();
  const client = useQueryClient();
  const liveScope = useQueryScope(workspaceSlug);
  const tenant = liveScope?.tenant;
  const principal = liveScope?.principal;
  const scope = useMemo<QueryScope | null>(
    () => (tenant !== undefined && principal !== undefined ? { tenant, principal } : null),
    [tenant, principal],
  );

  const cachedRole = useCallback(
    (id: string) =>
      scope
        ? client.getQueryData<TJobRole[]>(jobRolesKey(scope))?.find((r) => r.id === id)
        : undefined,
    [client, scope],
  );

  /** The members list shows each member's job role: re-read it. */
  const staleMembers = useCallback(
    (op: string) => {
      members.invalidateCachedMembers(workspaceSlug).catch((error: unknown) => {
        console.warn("[job-roles] marking cached members stale failed", {
          slug: workspaceSlug,
          op,
          error,
        });
      });
    },
    [members, workspaceSlug],
  );

  const createJobRole = useCallback<JobRoleWriters["createJobRole"]>(
    async (data, source = "page") => {
      const jobRole = await service.create(workspaceSlug, data);
      if (scope) patchJobRoles(client, scope, { written: jobRole });
      settle(client, scope, "create", jobRole.id);
      track("job_role_created", { job_role_id: jobRole.id, source });
      return jobRole;
    },
    [service, workspaceSlug, scope, client],
  );

  const updateJobRole = useCallback<JobRoleWriters["updateJobRole"]>(
    async (id, data, source = "page") => {
      // Read before the write: `fields` is what this save changed (NG-2063).
      const fields = changedFields(data, cachedRole(id));
      const jobRole = await service.update(workspaceSlug, id, data);
      if (scope) patchJobRoles(client, scope, { written: jobRole });
      settle(client, scope, "update", id);
      staleMembers("update");
      track("job_role_updated", { job_role_id: id, fields, source });
      return jobRole;
    },
    [cachedRole, service, workspaceSlug, scope, client, staleMembers],
  );

  const deleteJobRole = useCallback<JobRoleWriters["deleteJobRole"]>(
    async (id, source = "page") => {
      await service.remove(workspaceSlug, id);
      track("job_role_deleted", { job_role_id: id, source });
      if (scope) {
        patchJobRoles(client, scope, { deletedId: id });
        // A deleted role's members would re-read a 404: drop them first.
        client.removeQueries({ queryKey: jobRoleMembersKey(scope, id) });
      }
      settle(client, scope, "delete", id);
      staleMembers("delete");
      // The backend unassigns the role from every work item on delete; mirror
      // that in the work-item cache so already-loaded board/list/detail views
      // drop the role chip without a refetch.
      issueStore.clearJobRoleFromCache(id);
    },
    [service, workspaceSlug, scope, client, staleMembers, issueStore],
  );

  const setStatus = useCallback<JobRoleWriters["setStatus"]>(
    async (id, status, source = "page") => {
      const previous = cachedRole(id);
      if (!previous || !scope) return;
      const snapshot = previous.status;
      // A read in flight would land the old status over the new one.
      await client.cancelQueries({ queryKey: jobRolesKey(scope) });
      patchJobRoles(client, scope, { patched: id, patch: { status } });
      try {
        const jobRole = await service.update(workspaceSlug, id, { status });
        patchJobRoles(client, scope, { written: jobRole });
      } catch (error) {
        patchJobRoles(client, scope, { patched: id, patch: { status: snapshot } });
        throw error;
      }
      settle(client, scope, "status", id);
      if (status !== snapshot) {
        track(status === "inactive" ? "job_role_deactivated" : "job_role_activated", {
          job_role_id: id,
          source,
        });
      }
      // Deactivating (active -> inactive) unassigns the role from all work items
      // on the backend, same as delete — clear the work-item cache to match.
      if (snapshot === "active" && status === "inactive") {
        issueStore.clearJobRoleFromCache(id);
        staleMembers("deactivate");
      }
    },
    [cachedRole, scope, client, service, workspaceSlug, issueStore, staleMembers],
  );

  const setJobRoleMembers = useCallback<JobRoleWriters["setJobRoleMembers"]>(
    async (id, memberIds, source = "page") => {
      // The call sends the whole list: count what it adds and removes against
      // the members already read, before the write replaces them.
      const prior = scope
        ? (client.getQueryData<TJobRoleMember[]>(jobRoleMembersKey(scope, id)) ?? [])
            .filter((member) => member.assigned)
            .map((member) => member.id)
        : [];
      await service.setMembers(workspaceSlug, id, memberIds);
      const { added, removed } = listChanges(prior, memberIds);
      if (added > 0) track("job_role_assigned", { job_role_id: id, count: added, source });
      if (removed > 0) track("job_role_unassigned", { job_role_id: id, count: removed, source });
      if (scope) {
        // The closed cell's avatar stack shows the saved set at once.
        const chosen = new Set(memberIds);
        client.setQueryData<TJobRoleMember[]>(jobRoleMembersKey(scope, id), (current) =>
          current?.map((member) => ({ ...member, assigned: chosen.has(member.id) })),
        );
      }
      // The list's member counts, and every member whose job role moved.
      settle(client, scope, "set-members", id);
      staleMembers("set-members");
    },
    [scope, client, service, workspaceSlug, staleMembers],
  );

  const setMyJobRole = useCallback<JobRoleWriters["setMyJobRole"]>(
    async (jobRoleId, source = "page") => {
      const membership = await service.setMyJobRole(workspaceSlug, jobRoleId);
      if (scope) client.setQueryData(myJobRoleKey(scope), membership);
      settle(client, scope, "set-mine", jobRoleId ?? "");
      // The caller's own row in the member bank and the members table now
      // shows an old job role; the response is not that row, so mark them
      // stale rather than patch.
      staleMembers("set-mine");
      // A person setting their own job role.
      track("workspace_member_updated", { fields: ["job_role_id"], self: true, source });
      return membership;
    },
    [service, workspaceSlug, scope, client, staleMembers],
  );

  const smartAssign = useCallback<JobRoleWriters["smartAssign"]>(
    async (projectId, jobRoleId, assigneeId, assigneeDetail, source = "page") => {
      const result = await service.smartAssign(workspaceSlug, projectId, jobRoleId, assigneeId);
      // Reflect the bulk assignment in the work-item cache so the board/list/
      // table stop showing the (now-cleared) job role and show the assignee —
      // the views render from the cache, not from the recommendations re-read.
      issueStore.applySmartAssignToCache(result.work_item_ids, result.assignee_id, assigneeDetail);
      // The assignee's booked time moved, so the pick-someone-else list re-reads
      // their hours on its next open.
      if (scope) roleAssignmentCandidatesChanged(client, scope);
      // A task write: the tasks lane's bulk assignment shape, with the job role.
      track("task_assigned", {
        project_id: projectId,
        member_id: assigneeId,
        job_role_id: jobRoleId,
        count: result.work_item_ids.length,
        source,
      });
      // The role drops off once its work is staffed: re-read the banner's list.
      if (scope) {
        await client
          .refetchQueries({
            queryKey: projectJobRoleRecommendationsKey(scope, projectId),
            exact: true,
          })
          .catch((error: unknown) => {
            console.warn("[job-roles] re-reading recommendations failed", {
              slug: workspaceSlug,
              projectId,
              error,
            });
          });
      }
    },
    [service, workspaceSlug, issueStore, scope, client],
  );

  return useMemo(
    () => ({
      createJobRole,
      updateJobRole,
      deleteJobRole,
      setStatus,
      setJobRoleMembers,
      setMyJobRole,
      smartAssign,
    }),
    [
      createJobRole,
      updateJobRole,
      deleteJobRole,
      setStatus,
      setJobRoleMembers,
      setMyJobRole,
      smartAssign,
    ],
  );
}
